"use client";

import { create } from "zustand";
import { useAuthStore } from "./auth/store";
import { setPerfMarksEnabled } from "./perf-marks";

/**
 * Client-only feature flags, persisted per browser. Overrides are only active
 * for signed-in admins or the owner's account; everyone else gets defaults.
 */

export type FlagId =
  | "publicFeedbackPage"
  | "perfMarks"
  | "seedPicker"
  | "editorAssistant"
  | "componentAutoLayout"
  | "orthogonalCableRouter";

type FlagDef = {
  id: FlagId;
  label: string;
  description: string;
  default: boolean;
};

export const FLAG_REGISTRY: readonly FlagDef[] = [
  {
    id: "orthogonalCableRouter",
    label: "Experimental cable router",
    description: "Try new cable paths. Use Redraw all cables after switching to compare.",
    default: false,
  },
  {
    id: "componentAutoLayout",
    label: "Component auto-layout",
    description: "Arrange selected components and redraw cables. Runs automatically after AI adds components.",
    default: false,
  },
  {
    id: "editorAssistant",
    label: "In-editor assistant",
    description:
      "Ask about the current schematic and review suggested changes in a side panel or mobile chat. Sending requests and applying edits still require confirmation.",
    default: true,
  },
  {
    id: "publicFeedbackPage",
    label: "Publish for feedback",
    description:
      "Publish a public feedback page from the Share dialog. Existing public pages stay available when this is off.",
    default: true,
  },
  {
    id: "perfMarks",
    label: "Performance marks",
    description:
      "Adds timing measurements for diagnostics, simulation sweeps and cable layout to the browser's Performance panel. Takes effect immediately; may add a small overhead.",
    default: false,
  },
  {
    id: "seedPicker",
    label: "Project picker on first visit",
    description:
      "Show project start options on a fresh desktop visit. New project always offers examples and a blank canvas.",
    default: false,
  },
] as const;

const STORE_KEY = "crimp:feature-flags";

function defaultFlags(): Record<FlagId, boolean> {
  return Object.fromEntries(
    FLAG_REGISTRY.map((f) => [f.id, f.default]),
  ) as Record<FlagId, boolean>;
}

function loadFlags(): Record<FlagId, boolean> {
  const defaults = defaultFlags();
  if (typeof window === "undefined") return defaults;
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return defaults;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      for (const { id } of FLAG_REGISTRY) {
        const value = (parsed as Record<string, unknown>)[id];
        if (typeof value === "boolean") defaults[id] = value;
      }
    }
    return defaults;
  } catch {
    return defaults;
  }
}

export function canManageFeatureFlags(auth = useAuthStore.getState()): boolean {
  return Boolean(
    auth.ready && auth.session && (
      (auth.adminReady && auth.isAdmin) ||
      auth.session.user.email?.toLowerCase() === "danielhbappelgren@gmail.com"
    ),
  );
}

export function useCanManageFeatureFlags(): boolean {
  return useAuthStore(canManageFeatureFlags);
}

interface FeatureFlagsState {
  flags: Record<FlagId, boolean>;
  modalOpen: boolean;
  setFlag(id: FlagId, value: boolean): void;
  openModal(): void;
  closeModal(): void;
}

export const useFeatureFlags = create<FeatureFlagsState>((set, get) => ({
  flags: canManageFeatureFlags() ? loadFlags() : defaultFlags(),
  modalOpen: false,
  setFlag: (id, value) => {
    if (!canManageFeatureFlags()) return;
    const next = { ...get().flags, [id]: value };
    set({ flags: next });
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* ignore — flags revert to defaults next mount */
    }
  },
  openModal: () => {
    if (!canManageFeatureFlags()) return;
    set({ modalOpen: true });
  },
  closeModal: () => set({ modalOpen: false }),
}));

// Account changes must close an open panel and stop applying overrides. Keep
// the saved preferences so an authorized user can restore them on sign-in.
const unsubscribeAuth = useAuthStore.subscribe((auth, previous) => {
  const allowed = canManageFeatureFlags(auth);
  if (
    allowed === canManageFeatureFlags(previous) &&
    (!allowed || auth.session?.user.id === previous.session?.user.id)
  ) return;
  useFeatureFlags.setState({
    flags: allowed ? loadFlags() : defaultFlags(),
    modalOpen: false,
  });
});

// The timing helper stays dependency-free for the electrical engine and Workers.
setPerfMarksEnabled(useFeatureFlags.getState().flags.perfMarks);
const unsubscribeTiming = useFeatureFlags.subscribe(({ flags }) => {
  setPerfMarksEnabled(flags.perfMarks);
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeAuth();
    unsubscribeTiming();
  });
}

export function useFlag(id: FlagId): boolean {
  return useFeatureFlags((s) => s.flags[id]);
}

/**
 * A flag's shipped default, with no store and no React — the prerendered SKU
 * pages must render the same flag state the hydrated app will.
 */
export function flagDefault(id: FlagId): boolean {
  return FLAG_REGISTRY.find((flag) => flag.id === id)?.default ?? false;
}
